import React from 'react';
import { CalendarPlus, Loader2, AlertTriangle } from 'lucide-react';
import { moduleApi } from '../../lib/api';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';

interface Calendrier { id: string; month: number; year: number; status?: string | null }

const MOIS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
const libelleCalendrier = (c: Calendrier) => `${MOIS[Math.max(0, Math.min(11, (c.month || 1) - 1))]} ${c.year}`;

/**
 * LE GESTE QUE TOUT LE PARCOURS PRÉPARE : la privatisation entre enfin dans Événementiel.
 *
 * Emeni (2026-08-26) : « ce n'est qu'à ce moment-là qu'on est sûr qu'il privatise, donc on peut le mettre
 * dans le tableau événementiel ». Jusqu'ici le dossier n'avait ni date ni couverts — ils se sont récoltés au
 * rendez-vous et dans les échanges qui ont suivi, et c'est ici qu'ils se posent pour de bon.
 *
 * Cette étape ne passe volontairement PAS par la modale d'étape ordinaire : le serveur refuse d'atteindre
 * « Privatisation créée » par une simple transition, précisément parce qu'un événement sans date ni
 * calendrier n'a pas sa place dans Événementiel.
 *
 * Le calendrier n'est pas demandé à l'utilisateur en premier : il se déduit du mois de la date saisie —
 * c'est ainsi que les calendriers sont organisés dans Événementiel (un par mois). On ne montre le choix que
 * pour le corriger, et on refuse clairement si le mois n'a pas encore son calendrier, plutôt que de laisser
 * le serveur renvoyer une erreur incompréhensible.
 */
export function CreerPrivatisationModal({ label, saving, error, onCancel, onConfirm }: {
    label: string;
    saving: boolean;
    error: string;
    onCancel: () => void;
    onConfirm: (payload: Record<string, any>) => void;
}) {
    const [calendriers, setCalendriers] = React.useState<Calendrier[] | null>(null);
    const [date, setDate] = React.useState('');
    const [debut, setDebut] = React.useState('19:00');
    const [fin, setFin] = React.useState('23:00');
    const [couverts, setCouverts] = React.useState('');
    const [calendarId, setCalendarId] = React.useState('');
    const [choixManuel, setChoixManuel] = React.useState(false);

    React.useEffect(() => {
        moduleApi.getEvenementielCalendars()
            .then((r: any) => setCalendriers(Array.isArray(r) ? r : []))
            .catch(() => setCalendriers([]));
    }, []);

    // Le calendrier suit la date tant que l'utilisateur ne l'a pas choisi lui-même : changer la date d'un
    // mois ne doit pas laisser un calendrier de l'ancien mois silencieusement sélectionné.
    const calendrierDuMois = React.useMemo(() => {
        if (!date || !calendriers) return null;
        const d = new Date(`${date}T00:00:00`);
        if (isNaN(d.getTime())) return null;
        return calendriers.find((c) => c.year === d.getFullYear() && c.month === d.getMonth() + 1) || null;
    }, [date, calendriers]);

    React.useEffect(() => {
        if (!choixManuel) setCalendarId(calendrierDuMois?.id || '');
    }, [calendrierDuMois, choixManuel]);

    const passeMinuit = !!(date && debut && fin && fin < debut);
    const moisSansCalendrier = !!date && calendriers !== null && !calendrierDuMois && !choixManuel;
    const bloque = !date || !debut || !fin || !calendarId || saving;

    const champ = 'w-full bg-surface border border-border rounded-lg px-2.5 py-1.5 text-sm text-content outline-none focus:border-brand-500';

    return (
        <Modal open onClose={onCancel} title="Créer la privatisation" icon={<CalendarPlus size={16} />} maxWidth="max-w-md"
            footer={
                <>
                    <Button variant="secondary" onClick={onCancel} disabled={saving}>Annuler</Button>
                    <Button variant="primary" onClick={() => onConfirm({
                        date, start_time: debut, end_time: fin, calendar_id: calendarId,
                        num_people: couverts.trim() ? Number(couverts) : null,
                    })} disabled={bloque}>
                        {saving ? <Loader2 size={13} className="animate-spin" /> : null} Créer la privatisation
                    </Button>
                </>
            }>
            <div className="space-y-3">
                <p className="text-[11px] text-content-muted leading-relaxed p-2.5 rounded-xl bg-surface-soft border border-border">
                    Le devis de <span className="font-bold text-content">{label}</span> est signé : l'événement
                    peut maintenant entrer dans Événementiel avec sa vraie date. C'est le seul moment où l'on
                    est sûr qu'il aura lieu — d'où le fait qu'il n'y figurait pas avant.
                </p>

                <div className="space-y-1">
                    <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Date de la privatisation *</label>
                    <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={champ} autoFocus />
                </div>

                <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                        <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Début *</label>
                        <input type="time" value={debut} onChange={(e) => setDebut(e.target.value)} className={champ} />
                    </div>
                    <div className="space-y-1">
                        <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Fin *</label>
                        <input type="time" value={fin} onChange={(e) => setFin(e.target.value)} className={champ} />
                    </div>
                </div>

                {/* Un dîner qui se termine à 2h du matin est la règle, pas l'exception : on le dit au lieu de
                    laisser croire à une faute de saisie. Même comportement que la création manuelle. */}
                {passeMinuit && (
                    <p className="text-[11px] text-content-muted">
                        La fin est antérieure au début : l'événement se terminera le lendemain matin.
                    </p>
                )}

                <div className="space-y-1">
                    <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Nombre de couverts</label>
                    <input type="number" min={0} value={couverts} onChange={(e) => setCouverts(e.target.value)} className={champ} />
                    <p className="text-[10px] text-content-muted">
                        Laissez vide s'il n'est pas encore arrêté — il se complète dans la fiche de l'événement.
                    </p>
                </div>

                <div className="space-y-1">
                    <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Calendrier *</label>
                    {calendriers === null ? (
                        <div className="flex items-center gap-2 text-[11px] text-content-muted"><Loader2 size={12} className="animate-spin" /> Chargement des calendriers...</div>
                    ) : (
                        <select value={calendarId}
                            onChange={(e) => { setChoixManuel(true); setCalendarId(e.target.value); }}
                            className={champ}>
                            <option value="">—</option>
                            {calendriers.map((c) => (
                                <option key={c.id} value={c.id}>
                                    {libelleCalendrier(c)}{c.status === 'ARCHIVED' ? ' (archivé)' : ''}
                                </option>
                            ))}
                        </select>
                    )}
                    {calendrierDuMois && !choixManuel && (
                        <p className="text-[10px] text-content-muted">Choisi automatiquement d'après la date.</p>
                    )}
                </div>

                {moisSansCalendrier && (
                    <p className="text-[11px] text-amber-600 dark:text-amber-400 font-semibold flex items-start gap-1.5">
                        <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                        Aucun calendrier n'existe pour ce mois. Créez-le dans Événementiel, ou choisissez-en un
                        autre dans la liste ci-dessus.
                    </p>
                )}

                {error && <p className="text-xs text-red-500 font-semibold">{error}</p>}
            </div>
        </Modal>
    );
}
